import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FiMapPin, FiPlus, FiSearch, FiInfo, FiTrash2, FiX } from 'react-icons/fi';
import { useQuotesBookings } from './QuotesBookingsContext';
import AddLocationModal from './AddLocationModal';
import SelectLocationPrompt from './SelectLocationPrompt';
import { formatPhoneNumber } from '../../utils/phone';
import styles from './LocationPicker.module.css';

const getLocationName = (location) => location.name || location.company_name || 'Untitled location';

const buildAddressLine = (location) => {
  const parts = [location.street || location.address, location.city, location.state, location.zip].filter(Boolean);
  return parts.join(', ');
};

const getPrimaryContact = (location) => {
  if (location.contacts && location.contacts.length > 0) {
    return location.contacts[0];
  }
  return null;
};

const buildContactLine = (location) => {
  const primary = getPrimaryContact(location);
  const parts = [];
  if (primary) {
    if (primary.phone) {
      parts.push(primary.ext ? `${primary.phone} x${primary.ext}` : primary.phone);
    }
    if (primary.email) {
      parts.push(primary.email);
    }
  } else {
    const contacts = [formatPhoneNumber(location.phone || ''), location.email].filter(Boolean);
    parts.push(...contacts);
  }
  return parts.join(' · ');
};

const buildDockTooltip = (location) => {
  const type = location.dockType || 'Unknown';
  const hours = [location.dockHoursFrom, location.dockHoursTo].filter(Boolean).join(' - ');
  if (hours) {
    return `${type} ${hours}`;
  }
  return type;
};

const LocationSelector = ({ label, value, onChange, renderTrigger = true, openSignal = null }) => {
  const {
    myLocations,
    allLocations,
    addLocation,
    removeLocationFromMy,
    attachLocationToMy,
    isLoadingLocations,
    locationsError,
  } = useQuotesBookings();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('my');
  const [query, setQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [contactDetailFor, setContactDetailFor] = useState(null);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [showSelectionPrompt, setShowSelectionPrompt] = useState(false);

  useEffect(() => {
    if (openSignal) {
      setIsOpen(true);
    }
  }, [openSignal]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setContactDetailFor(null);
        setDeleteError('');
        setSaveError('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const locations = activeTab === 'my' ? myLocations : allLocations;

  const filteredLocations = useMemo(() => {
    if (!query) return locations;
    return locations.filter((location) =>
      [location.name, location.street, location.city, location.state, location.zip]
        .join(' ')
        .toLowerCase()
        .includes(query.toLowerCase())
    );
  }, [locations, query]);

  const handleSelect = async (location) => {
    setDeleteError('');
    let chosen = location;
    if (activeTab === 'all' && !myLocations.some((loc) => String(loc.id) === String(location.id))) {
      try {
        chosen = await attachLocationToMy(location.id);
      } catch (error) {
        console.error('Failed to attach location to My Locations', error);
        setDeleteError('Unable to add this location to My Locations right now.');
      }
    }
    onChange(chosen);
    setIsOpen(false);
  };

  const handleAddLocation = async (draftLocation) => {
    setSaveError('');
    setDeleteError('');
    try {
      const created = await addLocation(draftLocation);
      setIsAddModalOpen(false);
      setActiveTab('my');
      setPendingSelection(created);
      setShowSelectionPrompt(true);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to save location', error);
      setSaveError('Unable to save this location. Please try again.');
      throw error;
    }
  };

  const handlePromptConfirm = async (shouldApply) => {
    if (shouldApply && pendingSelection) {
      await handleSelect(pendingSelection);
    }
    setPendingSelection(null);
    setShowSelectionPrompt(false);
  };

  const handlePromptDismiss = () => {
    setPendingSelection(null);
    setShowSelectionPrompt(false);
  };

  const handleRemove = async (event, locationId) => {
    event.preventDefault();
    event.stopPropagation();
    setDeleteError('');
    setDeletingId(locationId);
    try {
      await removeLocationFromMy(locationId);
    } catch (error) {
      console.error('Failed to remove location', error);
      setDeleteError('Unable to remove this location from My Locations.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={styles.wrapper}>
      {renderTrigger && (
        <label className={styles.label}>
          {label || 'Location'}
          <div className={styles.selectionActions}>
            <button
              type="button"
              className={[styles.trigger, value ? styles.triggerSelected : ''].filter(Boolean).join(' ')}
              onClick={() => setIsOpen(true)}
            >
              <FiMapPin />
              <div>
                <span>{value ? getLocationName(value) : 'Select a saved location'}</span>
                {value ? (
                  <>
                    {buildAddressLine(value) && <small className={styles.selectionMeta}>{buildAddressLine(value)}</small>}
                    {buildContactLine(value) && <small className={styles.selectionMeta}>{buildContactLine(value)}</small>}
                  </>
                ) : (
                  <small>Browse saved facilities or add a new one.</small>
                )}
              </div>
              {value && (
                <button
                  type="button"
                  className={styles.inlineRemove}
                  aria-label="Remove selected location"
                  onClick={(event) => {
                    event.stopPropagation();
                    onChange(null);
                  }}
                >
                  <FiX />
                </button>
              )}
            </button>
          </div>
        </label>
      )}

      {!renderTrigger && (
        <button
          type="button"
          className={styles.hiddenTrigger}
          onClick={() => setIsOpen(true)}
          aria-label="Edit location"
        />
      )}

      {isOpen && (
        <div
          className={styles.dialogOverlay}
          role="dialog"
          aria-modal="true"
          onClick={() => setIsOpen(false)}
        >
          <div
            className={styles.dialog}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.dialogHeader}>
              <div>
                <h4>Select location</h4>
                <p>Locations persist across your workspace for rapid tender creation.</p>
              </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => {
                setIsOpen(false);
                setDeleteError('');
                setSaveError('');
                setContactDetailFor(null);
              }}
            >
                ×
              </button>
            </div>
            <div className={styles.controls}>
              <div className={styles.tabs}>
                <button
                  type="button"
                  className={[styles.tab, activeTab === 'my' ? styles.active : ''].filter(Boolean).join(' ')}
                  onClick={() => setActiveTab('my')}
                >
                  My locations
                </button>
                <button
                  type="button"
                  className={[styles.tab, activeTab === 'all' ? styles.active : ''].filter(Boolean).join(' ')}
                  onClick={() => setActiveTab('all')}
                >
                  All locations
                </button>
              </div>
              <div className={styles.search}>
                <FiSearch />
                <input
                  placeholder="Search name, city, or ZIP"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
            </div>

          <div className={styles.list}>
            {isLoadingLocations && <div className={styles.emptyState}>Loading saved locations…</div>}
            {locationsError && <div className={styles.error}>{locationsError}</div>}

            {!isLoadingLocations && !locationsError && filteredLocations.map((location) => {
              const isSelected = value && String(value.id) === String(location.id);
              const addressLine = buildAddressLine(location);
              const contactLine = buildContactLine(location);
              const hasMultipleContacts = (location.contacts || []).length > 1;
              const dockType = location.dockType || 'Unknown';
              const dockHoursLine = [location.dockHoursFrom, location.dockHoursTo].filter(Boolean).join(' - ');
              return (
                <div
                  role="button"
                  tabIndex={0}
                  key={location.id}
                  className={[styles.listItem, isSelected ? styles.listItemSelected : ''].filter(Boolean).join(' ')}
                  onClick={() => handleSelect(location)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleSelect(location);
                    }
                  }}
                >
                  <div className={styles.listRow}>
                    <div className={styles.listLeft}>
                      <div className={styles.listTitleRow}>
                        <div className={styles.listTitle}>{getLocationName(location)}</div>
                        <button
                          type="button"
                          className={styles.infoIconButton}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            console.log('Location profile coming soon');
                          }}
                          aria-label="Open location profile"
                        >
                          <FiInfo />
                        </button>
                      </div>
                      <div className={styles.listMetaLine}>
                        {[addressLine, contactLine].filter(Boolean).join(' · ')}
                        {hasMultipleContacts && (
                          <button
                            type="button"
                            className={styles.seeMore}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setContactDetailFor(
                                contactDetailFor === location.id ? null : location.id
                              );
                            }}
                          >
                            · See others
                          </button>
                        )}
                      </div>
                    </div>
                    <div className={styles.listDock} title={buildDockTooltip(location)}>
                      <span className={styles.quiet}>Dock</span>
                      <span>{dockType}</span>
                      {dockHoursLine && <small className={styles.quiet}>{dockHoursLine}</small>}
                    </div>
                  </div>
                  {contactDetailFor === location.id && (
                    <div className={styles.contactsPopover}>
                      <div className={styles.contactsTable}>
                        <div className={styles.contactsHeaderRow}>
                          <span>Name</span>
                          <span>Role</span>
                          <span>Phone</span>
                          <span>Ext</span>
                          <span>Email</span>
                          <span>Notes</span>
                        </div>
                        {location.contacts?.map((contact) => (
                          <div key={contact.id || contact.email || contact.name} className={styles.contactsRow}>
                            <span>{contact.name || '—'}</span>
                            <span>{contact.role || '—'}</span>
                            <span>{contact.phone || '—'}</span>
                            <span>{contact.ext || '—'}</span>
                            <span>{contact.email || '—'}</span>
                            <span>{contact.notes || '—'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {activeTab === 'my' && (
                    <div className={styles.listActions}>
                      <button
                        type="button"
                        className={styles.deleteLocation}
                        onClick={(event) => handleRemove(event, location.id)}
                        disabled={deletingId === location.id}
                      >
                        <FiTrash2 /> {deletingId === location.id ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {!isLoadingLocations && !locationsError && filteredLocations.length === 0 && (
              <div className={styles.emptyState}>
                <h5>No saved locations found</h5>
                <p>Try a different search or add a new facility.</p>
              </div>
            )}
          </div>

          <button type="button" className={styles.addButton} onClick={() => setIsAddModalOpen(true)}>
            <FiPlus /> Add new location
          </button>
          {deleteError && <p className={styles.error}>{deleteError}</p>}
          {saveError && <p className={styles.error}>{saveError}</p>}
          </div>
        </div>
      )}

      <AddLocationModal
        isOpen={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        onSave={handleAddLocation}
      />
      <SelectLocationPrompt
        isOpen={showSelectionPrompt}
        location={pendingSelection}
        onConfirm={handlePromptConfirm}
        onDismiss={handlePromptDismiss}
      />
    </div>
  );
};

LocationSelector.propTypes = {
  label: PropTypes.string,
  value: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    dockHoursFrom: PropTypes.string,
    dockHoursTo: PropTypes.string,
    dockType: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  renderTrigger: PropTypes.bool,
  openSignal: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
};

LocationSelector.defaultProps = {
  label: 'Location',
  value: null,
  renderTrigger: true,
  openSignal: null,
};

export default LocationSelector;
